/**
 * Per-doctype prices, taken STRICTLY from the live `doctypes/doc_pricing.json`.
 *
 * The add-in prices from `GET /doc-types` (`document_price_minor` /
 * `section_price_minor`, loader-derived from the live file), NOT from the rate
 * card — the rate card serves image-baked constants that only equal the file via
 * a CI pin. The Dodo charge is still computed server-side; these numbers are what
 * the writer is shown next to the decision that determines them.
 *
 * Pure and fetch-free so the fold is testable without a server.
 */

/**
 * The flat "build on your own document" unlock, in minor units (USD cents).
 *
 * A mirror of the backend's `rate_card.CUSTOM_PRICE_MINOR`, shown next to the
 * paywall; the charge itself is computed server-side. A custom submission is
 * priced the same whatever doctype it is tagged as, so this is a constant here
 * rather than a `/doc-types` lookup.
 */
export const CUSTOM_UNLOCK_PRICE_MINOR = 25_000;

/** One doctype's two prices, in minor units (USD cents). */
export interface DocPrice {
  readonly documentMinor: number;
  /**
   * `null` when this doctype has no section product (sold whole only). Not zero,
   * and not a fallback to the document price — a screen must tell "a section
   * costs nothing" from "a section cannot be bought", and only `null` says the
   * second.
   */
  readonly sectionMinor: number | null;
}

/** One `/doc-types` catalogue entry, narrowed to what pricing needs. */
export interface DocTypePriceEntry {
  readonly code: string;
  readonly document_price_minor?: number | null;
  readonly section_price_minor?: number | null;
  readonly self_serve?: boolean;
}

/**
 * The price for one doctype, or `undefined` when it is not on sale.
 *
 * `undefined` (not a zeroed price) is the honest render for a doctype off the
 * pricing allowlist — its `document_price_minor` is `null` — and it is the same
 * path a deployment with no provider keys takes, so every caller already handles
 * a missing price by drafting for free.
 */
export function docPriceOf(entry: DocTypePriceEntry | undefined): DocPrice | undefined {
  if (entry === undefined) return undefined;
  const doc = entry.document_price_minor;
  if (doc === null || doc === undefined) return undefined;
  return {
    documentMinor: doc,
    sectionMinor: entry.section_price_minor ?? null,
  };
}

/** Index `/doc-types` entries by code, so a caller can `.get(docType)`. */
export function indexDocPrices(entries: readonly DocTypePriceEntry[]): ReadonlyMap<string, DocPrice> {
  const out = new Map<string, DocPrice>();
  for (const entry of entries) {
    const price = docPriceOf(entry);
    if (price !== undefined) out.set(entry.code, price);
  }
  return out;
}

/**
 * Is this doctype sold only with a person in the loop?
 *
 * `doc_pricing.json` is the **self-serve allowlist** — which doctypes a stranger
 * may buy with no sales conversation (every public doctype except `CRF`; see `doctypes/doc_pricing.json`). The
 * server's rate card prices twenty-odd, and the payment gate asks *that* one:
 * `_can_be_sold` resolves a real product for `CER`, so a billable workspace
 * drafting one is refused 402. But its `document_price_minor` is `null` here, so
 * `docPriceOf` returns `undefined` — and every caller reads a missing price as
 * "not on sale → free".
 *
 * That conflates two opposite things. **Not priced anywhere** is free; **priced,
 * but not by the allowlist** is sales-assisted, and drafting it walks into a 402
 * with no checkout to clear it. `self_serve` is what tells them apart, and the
 * web wizard already branches on it (`NewDocumentPage`'s `salesAssisted`).
 *
 * Strictly `=== false`, so a server that omits the field (or a doctype missing
 * from the catalogue entirely) fails open rather than blocking a draft.
 */
export function isSalesAssisted(entry: DocTypePriceEntry | undefined): boolean {
  return entry?.self_serve === false;
}
